/**
 * TeamMemberDrawer — 7-tab polished profile workspace (Phase G.2).
 * Tabs:
 *   Overview · Employment · Departments · Access · Security · Activity · Audit
 * Deferred (later phases): Documents · Payroll · Notes · Settings · Emergency
 */
import { useEffect, useMemo, useState } from "react";
import {
  User, Briefcase, Building2, ShieldCheck, Lock, Activity, ScrollText,
  Star, StarOff, Trash2, Plus, RefreshCw, Info, Check, X, AlertTriangle,
  KeyRound as KeyRoundIcon, Gauge,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/primitives";
import ReadinessTab from "./ReadinessTab";
import { DatePicker } from "@/components/ui/date-picker";

/* ── shared bits ─────────────────────────────────────────────────────── */
const STATUS_TONE = {
  active:        "success",
  suspended:     "error",
  on_leave:      "warning",
  notice_period: "warning",
  terminated:    "neutral",
};

function Row({ label, value, mono = false, testid }) {
  return (
    <div className="flex items-start justify-between gap-6 py-2.5 border-b border-hocp-alice/60 last:border-b-0"
         data-testid={testid}>
      <div className="text-[12px] uppercase tracking-[0.10em] text-hocp-textTertiary font-semibold pt-0.5">
        {label}
      </div>
      <div className={`text-[14px] text-hocp-textPrimary text-right ${mono ? "font-mono tabular-nums" : ""}`}>
        {value || <span className="text-hocp-textTertiary">—</span>}
      </div>
    </div>
  );
}

function TabButton({ id, label, icon: Icon, active, onClick, testid }) {
  return (
    <button
      onClick={() => onClick(id)}
      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-[13.5px] font-medium transition
        ${active
          ? "bg-hocp-gold text-hocp-navy shadow-sm"
          : "text-hocp-textSecondary hover:bg-hocp-alice/60 hover:text-hocp-textPrimary"}`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
      <span>{label}</span>
    </button>
  );
}

function Avatar({ name, photo_url, size = 72 }) {
  const initials = (name || "?").split(" ").filter(Boolean).slice(0, 2)
                                  .map(w => w[0].toUpperCase()).join("");
  if (photo_url) {
    return <img src={photo_url} alt={name} style={{ width: size, height: size }}
                 className="rounded-2xl object-cover ring-2 ring-white shadow-hocp-sm"/>;
  }
  return (
    <div style={{ width: size, height: size }}
         className="rounded-2xl bg-gradient-to-br from-hocp-honeydew to-hocp-vanilla text-hocp-navy
                    flex items-center justify-center font-display font-bold text-[26px] shadow-hocp-sm ring-2 ring-white">
      {initials || "?"}
    </div>
  );
}

function DeptChip({ a, onReprimary, onRevoke, canReprimary, canRevoke }) {
  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-hocp-border hover:bg-hocp-alice/25 transition"
         data-testid={`dept-chip-${a.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-[14px] text-hocp-textPrimary">
            {a.department_name || a.department_code || "Unknown"}
          </span>
          {a.is_primary && (
            <Badge tone="accent" size="sm">
              <Star className="w-3 h-3 mr-0.5" strokeWidth={2}/> Primary
            </Badge>
          )}
          {a.status === "scheduled" && <Badge tone="warning" size="sm">Scheduled</Badge>}
          {a.status === "expired" && <Badge tone="neutral" size="sm">Expired</Badge>}
          {a.status === "revoked" && <Badge tone="error" size="sm">Revoked</Badge>}
        </div>
        <div className="text-[12px] text-hocp-textSecondary mt-0.5 flex items-center gap-2">
          {a.role && <span>{a.role}</span>}
          <span className="tabular-nums">
            {a.start_date}
            {a.end_date ? ` → ${a.end_date}` : " → open"}
          </span>
          {a.reason && a.reason !== "hired" && <span className="text-hocp-textTertiary">· {a.reason}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!a.is_primary && a.status === "active" && canReprimary && (
          <Button variant="ghost" size="sm" onClick={() => onReprimary(a)} data-testid={`reprimary-${a.id}`}>
            <Star className="w-3.5 h-3.5 mr-1" strokeWidth={2}/> Make Primary
          </Button>
        )}
        {a.status === "active" && canRevoke && (
          <Button variant="ghost" size="sm" onClick={() => onRevoke(a)} data-testid={`revoke-${a.id}`}>
            <Trash2 className="w-3.5 h-3.5"/>
          </Button>
        )}
      </div>
    </div>
  );
}

/* ── tabs ────────────────────────────────────────────────────────────── */
function OverviewTab({ tm }) {
  return (
    <div className="space-y-6">
      <div className="hocp-card p-5 space-y-1">
        <Row label="Full Name" value={tm.name} testid="row-name"/>
        <Row label="Email" value={tm.email} testid="row-email"/>
        <Row label="Employee ID" value={tm.employee_id} mono testid="row-emp-id"/>
        <Row label="Designation" value={tm.designation} testid="row-designation"/>
        <Row label="Platform Role" value={tm.role_name} testid="row-role"/>
        <Row label="Primary Department" value={tm.primary_department_name || "—"} testid="row-primary-dept"/>
        <Row label="Joined" value={tm.joining_date || (tm.created_at || "").slice(0, 10)} testid="row-joined"/>
      </div>
    </div>
  );
}

function EmploymentTab({ tm, employmentTypes, canEdit, onSave }) {
  const [designation, setDesignation] = useState(tm.designation || "");
  const [empType,     setEmpType]     = useState(tm.employment_type || "PERMANENT");
  const [empStatus,   setEmpStatus]   = useState(tm.employment_status || "active");
  const [joining,     setJoining]     = useState(tm.joining_date || "");
  const [exit,        setExit]        = useState(tm.exit_date || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        designation, employment_type: empType, employment_status: empStatus,
        joining_date: joining || null, exit_date: exit || null,
      });
      toast.success("Employment details saved");
    } catch (e) { toast.error(formatApiError(e) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="hocp-card p-5 space-y-4" data-testid="employment-tab">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-hocp-textTertiary font-semibold">Designation</label>
          <Input value={designation} onChange={e => setDesignation(e.target.value)} disabled={!canEdit}
                  data-testid="employment-designation" className="mt-1"/>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-hocp-textTertiary font-semibold">Employment Type</label>
          <Select value={empType} onValueChange={setEmpType} disabled={!canEdit}>
            <SelectTrigger className="mt-1" data-testid="employment-type"><SelectValue/></SelectTrigger>
            <SelectContent>
              {employmentTypes.map(t => (
                <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-hocp-textTertiary font-semibold">Employment Status</label>
          <Select value={empStatus} onValueChange={setEmpStatus} disabled={!canEdit}>
            <SelectTrigger className="mt-1" data-testid="employment-status"><SelectValue/></SelectTrigger>
            <SelectContent>
              {["active","on_leave","notice_period","suspended","terminated"].map(s => (
                <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-hocp-textTertiary font-semibold">Joining Date</label>
          <DatePicker value={(joining || '').slice(0,10)} onChange={(_v) => setJoining(_v)} disabled={!canEdit} data-testid="employment-joining"/>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-hocp-textTertiary font-semibold">Exit Date</label>
          <DatePicker value={(exit || '').slice(0,10)} onChange={(_v) => setExit(_v)} disabled={!canEdit} data-testid="employment-exit"/>
        </div>
      </div>
      {canEdit && (
        <div className="flex justify-end pt-2">
          <Button size="sm" onClick={save} disabled={saving} data-testid="employment-save">
            {saving ? "Saving…" : "Save Employment"}
          </Button>
        </div>
      )}
    </div>
  );
}

function DepartmentsTab({ tm, departments, canAssign, canRevoke, canReprimary, refresh }) {
  const [openAssign, setOpenAssign] = useState(false);
  const [deptId, setDeptId] = useState("");
  const [role, setRole] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("hired");
  const [saving, setSaving] = useState(false);

  const active = tm.active_assignments || [];
  const usedDeptIds = new Set(active.map(a => a.department_id));
  const availableDepts = departments.filter(d => !usedDeptIds.has(d.id));

  const assign = async () => {
    if (!deptId) return toast.error("Pick a department");
    setSaving(true);
    try {
      await api.post(`/team-members/${tm.id}/assignments`, {
        department_id: deptId, role: role || null, is_primary: isPrimary,
        start_date: startDate, end_date: endDate || null, reason,
      });
      toast.success("Department assigned");
      setOpenAssign(false);
      setDeptId(""); setRole(""); setIsPrimary(false); setEndDate("");
      setReason("hired");
      refresh();
    } catch (e) { toast.error(formatApiError(e) || "Failed to assign"); }
    finally { setSaving(false); }
  };

  const onReprimary = async (a) => {
    try {
      await api.post(`/team-members/${tm.id}/assignments/reprimary`, { assignment_id: a.id });
      toast.success(`${a.department_name} is now the primary department`);
      refresh();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  const onRevoke = async (a) => {
    const reason = window.prompt(`Revoke assignment to ${a.department_name}? Reason:`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/assignments/${a.id}/revoke`, { reason });
      toast.success("Assignment revoked");
      refresh();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="departments-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-semibold text-[16px] text-hocp-textPrimary">Department Assignments</div>
          <div className="text-[12px] text-hocp-textSecondary">Multi-department, time-bound. Exactly one primary.</div>
        </div>
        {canAssign && (
          <Button size="sm" onClick={() => setOpenAssign(!openAssign)} data-testid="add-assignment">
            <Plus className="w-4 h-4 mr-1"/> Assign Department
          </Button>
        )}
      </div>

      {openAssign && (
        <div className="hocp-card p-4 space-y-3 border-hocp-honeydew" data-testid="assign-dept-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase text-hocp-textTertiary font-semibold">Department</label>
              <Select value={deptId} onValueChange={setDeptId}>
                <SelectTrigger className="mt-1" data-testid="assign-dept-select"><SelectValue placeholder="Choose…"/></SelectTrigger>
                <SelectContent>
                  {availableDepts.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name} ({d.code})</SelectItem>
                  ))}
                  {availableDepts.length === 0 && (
                    <div className="px-3 py-2 text-[12px] text-hocp-textSecondary">All departments already assigned.</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase text-hocp-textTertiary font-semibold">Role Label</label>
              <Input value={role} onChange={e => setRole(e.target.value)}
                      placeholder="supervisor / attendant / manager…" className="mt-1" data-testid="assign-role"/>
            </div>
            <div>
              <label className="text-[11px] uppercase text-hocp-textTertiary font-semibold">Start Date</label>
              <DatePicker value={(startDate || '').slice(0,10)} onChange={(_v) => setStartDate(_v)} data-testid="assign-start"/>
            </div>
            <div>
              <label className="text-[11px] uppercase text-hocp-textTertiary font-semibold">End Date (optional)</label>
              <DatePicker value={(endDate || '').slice(0,10)} onChange={(_v) => setEndDate(_v)} data-testid="assign-end"/>
            </div>
            <div>
              <label className="text-[11px] uppercase text-hocp-textTertiary font-semibold">Reason</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="mt-1" data-testid="assign-reason"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["hired", "seasonal", "weekend_cover", "temporary_backfill", "cross_training"].map(r => (
                    <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <label className="text-[13px] text-hocp-textPrimary flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)}
                        data-testid="assign-primary"/>
                Set as primary
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpenAssign(false)}>Cancel</Button>
            <Button size="sm" onClick={assign} disabled={saving} data-testid="assign-confirm">
              {saving ? "Assigning…" : "Assign"}
            </Button>
          </div>
        </div>
      )}

      {active.length === 0 && (
        <div className="hocp-card p-8 text-center text-hocp-textSecondary">
          <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" strokeWidth={1.5}/>
          No active department assignments.
        </div>
      )}

      {active.length > 0 && (
        <div className="space-y-2" data-testid="assignment-list">
          {active.map(a => (
            <DeptChip key={a.id} a={a}
                       onReprimary={onReprimary} onRevoke={onRevoke}
                       canReprimary={canReprimary} canRevoke={canRevoke}/>
          ))}
        </div>
      )}
    </div>
  );
}

function AccessTab({ tm, templates, canApply, refresh }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [chosen, setChosen] = useState("");
  const [applyDefaults, setApplyDefaults] = useState(false);
  const [saving, setSaving] = useState(false);

  const snapshot = tm.access_template_snapshot;

  const apply = async () => {
    if (!chosen) return toast.error("Pick a template");
    setSaving(true);
    try {
      await api.post(`/team-members/${tm.id}/apply-template`, {
        template_id: chosen,
        apply_default_departments: applyDefaults,
      });
      toast.success("Access template applied");
      setPickerOpen(false); setChosen(""); setApplyDefaults(false);
      refresh();
    } catch (e) { toast.error(formatApiError(e) || "Failed to apply"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="access-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-semibold text-[16px] text-hocp-textPrimary">Access Template</div>
          <div className="text-[12px] text-hocp-textSecondary">One-click assignment. Snapshot captured at apply time.</div>
        </div>
        {canApply && (
          <Button size="sm" onClick={() => setPickerOpen(!pickerOpen)} data-testid="change-template">
            <RefreshCw className="w-4 h-4 mr-1"/> {snapshot ? "Change Template" : "Assign Template"}
          </Button>
        )}
      </div>

      {pickerOpen && (
        <div className="hocp-card p-4 space-y-3 border-hocp-honeydew" data-testid="template-picker">
          <div>
            <label className="text-[11px] uppercase text-hocp-textTertiary font-semibold">Choose Template</label>
            <Select value={chosen} onValueChange={setChosen}>
              <SelectTrigger className="mt-1" data-testid="template-select"><SelectValue placeholder="Search templates…"/></SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name} · {t.platform_role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {chosen && (
            <div className="rounded-lg bg-hocp-alice/40 p-3 text-[12px] text-hocp-textSecondary">
              <div className="flex items-center gap-1 mb-1 text-hocp-textPrimary font-semibold">
                <Info className="w-3.5 h-3.5"/> What this template does
              </div>
              {(() => {
                const t = templates.find(x => x.id === chosen);
                if (!t) return null;
                return (
                  <div className="space-y-1">
                    <div>Platform Role: <strong className="text-hocp-textPrimary">{t.platform_role}</strong></div>
                    <div>{t.extra_permissions.length} extra permissions inherited</div>
                    {t.default_departments?.length > 0 && (
                      <div>Suggested departments: <span className="text-hocp-textPrimary">{t.default_departments.join(", ")}</span></div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          <label className="text-[13px] flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={applyDefaults} onChange={e => setApplyDefaults(e.target.checked)}
                    data-testid="apply-defaults"/>
            Also assign template&apos;s default departments
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setPickerOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={apply} disabled={saving} data-testid="apply-template-confirm">
              {saving ? "Applying…" : "Apply Template"}
            </Button>
          </div>
        </div>
      )}

      {/* Explain-the-chain diagram */}
      <div className="hocp-card p-5" data-testid="access-chain">
        <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary font-semibold mb-3">
          Access Chain
        </div>
        <div className="flex items-center gap-3 flex-wrap text-[13px]">
          <div className="px-3 py-2 rounded-lg bg-hocp-honeydew/15 border border-hocp-honeydew/40">
            <div className="text-[10px] uppercase text-hocp-textTertiary">Access Template</div>
            <div className="font-semibold text-hocp-textPrimary">{snapshot?.name || <span className="text-hocp-textTertiary">Not set</span>}</div>
          </div>
          <span className="text-hocp-textTertiary">↓</span>
          <div className="px-3 py-2 rounded-lg bg-hocp-alice/40 border border-hocp-border">
            <div className="text-[10px] uppercase text-hocp-textTertiary">Platform Role</div>
            <div className="font-semibold text-hocp-textPrimary">{tm.role_name || "user"}</div>
          </div>
          <span className="text-hocp-textTertiary">↓</span>
          <div className="px-3 py-2 rounded-lg bg-hocp-alice/40 border border-hocp-border">
            <div className="text-[10px] uppercase text-hocp-textTertiary">Extras inherited</div>
            <div className="font-semibold text-hocp-textPrimary tabular-nums">{snapshot?.extra_permissions?.length || 0}</div>
          </div>
          {tm.permission_overrides?.length > 0 && (
            <>
              <span className="text-hocp-textTertiary">+</span>
              <div className="px-3 py-2 rounded-lg bg-hocp-vanilla/25 border border-hocp-vanilla">
                <div className="text-[10px] uppercase text-hocp-textTertiary">Direct Overrides</div>
                <div className="font-semibold text-hocp-textPrimary tabular-nums">{tm.permission_overrides.length}</div>
              </div>
            </>
          )}
        </div>
      </div>

      {snapshot?.extra_permissions?.length > 0 && (
        <div className="hocp-card p-5" data-testid="template-perms">
          <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary font-semibold mb-3">
            Permissions from Template
          </div>
          <div className="flex flex-wrap gap-1.5">
            {snapshot.extra_permissions.map(p => (
              <span key={p} className="text-[11px] font-mono px-2 py-1 rounded-md bg-hocp-alice/60 text-hocp-textPrimary">
                {p}
              </span>
            ))}
          </div>
        </div>
      )}

      {tm.permission_overrides?.length > 0 && (
        <div className="hocp-card p-5" data-testid="direct-overrides">
          <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary font-semibold mb-3">
            Direct Overrides (outside template)
          </div>
          <div className="flex flex-wrap gap-1.5">
            {tm.permission_overrides.map(p => (
              <span key={p} className="text-[11px] font-mono px-2 py-1 rounded-md bg-hocp-vanilla/40 text-hocp-textPrimary">
                {p}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SecurityTab({ tm, isSuperAdmin, refresh }) {
  const [sec, setSec] = useState(null);
  const [timeline, setTimeline] = useState(null);

  const [showReset, setShowReset] = useState(false);
  const [newPass, setNewPass] = useState("");
  const [forceChange, setForceChange] = useState(true);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: s } = await api.get(`/team-members/${tm.id}/security`);
      setSec(s);
      const { data: t } = await api.get(`/team-members/${tm.id}/security-timeline?limit=100`);
      setTimeline(t.items || []);
    } catch (e) { toast.error(formatApiError(e) || "Failed to load security"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tm.id]);

  const doReset = async () => {
    if (newPass.length < 12) return toast.error("Password must be >=12 characters");
    setSaving(true);
    try {
      const { data } = await api.post(`/users/${tm.id}/admin-reset-password`, {
        new_password: newPass, force_password_change: forceChange, reason: reason || null,
      });
      toast.success(`Password reset · ${data.sessions_revoked || 0} sessions revoked`);
      setShowReset(false); setNewPass(""); setReason(""); setForceChange(true);
      await load(); refresh?.();
    } catch (e) { toast.error(formatApiError(e) || "Reset failed"); }
    finally { setSaving(false); }
  };

  const doUnlock = async () => {
    const r = window.prompt("Reason for unlock (optional):") || "";
    try {
      await api.post(`/team-members/${tm.id}/unlock`, { reason: r || null });
      toast.success("Account unlocked");
      await load(); refresh?.();
    } catch (e) { toast.error(formatApiError(e) || "Unlock failed"); }
  };

  const doTerminate = async (jti) => {
    const r = window.prompt("Reason for terminating this session:");
    if (!r || r.trim().length < 3) return;
    try {
      await api.post(`/sessions/${jti}/terminate`, { reason: r });
      toast.success("Session terminated"); await load();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  const doTerminateAll = async () => {
    if (!window.confirm(`End ALL active sessions for ${tm.name}?`)) return;
    const r = window.prompt("Reason:");
    if (!r || r.trim().length < 3) return;
    try {
      const { data } = await api.post(`/team-members/${tm.id}/sessions/terminate-all`, { reason: r });
      toast.success(`Terminated ${data.revoked_count} session(s)`); await load();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  const ACCT_TONE = {
    active: "success", suspended: "warning", locked: "error",
    pending_password_change: "warning", inactive: "neutral", archived: "neutral",
  };

  return (
    <div className="space-y-4" data-testid="security-tab">
      {/* Account state block */}
      <div className="hocp-card p-5 space-y-1" data-testid="security-state">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display font-semibold text-[15px] text-hocp-textPrimary">Account State</div>
          {sec?.account_status === "locked" && isSuperAdmin && (
            <Button size="sm" onClick={doUnlock} data-testid="admin-unlock">
              <KeyRoundIcon className="w-3.5 h-3.5 mr-1"/> Unlock account
            </Button>
          )}
        </div>
        <Row label="Account Status" value={
          <Badge tone={ACCT_TONE[sec?.account_status] || "neutral"} size="md">
            {sec?.account_status || "—"}
          </Badge>
        }/>
        <Row label="Employment Status" value={
          <Badge tone={STATUS_TONE[tm.employment_status] || "neutral"} size="md">
            {tm.employment_status}
          </Badge>
        }/>
        <Row label="Failed Login Count" value={
          <span className={`tabular-nums font-semibold ${sec?.failed_login_count > 0 ? "text-hocp-error" : ""}`}>
            {sec?.failed_login_count ?? 0}
          </span>
        }/>
        <Row label="Last Failed Login" value={sec?.last_failed_login_at ? sec.last_failed_login_at.slice(0, 19).replace("T", " ") : "—"}/>
        {sec?.lock_reason && (
          <Row label="Lock Reason" value={<span className="text-hocp-error">{sec.lock_reason}</span>}/>
        )}
        {sec?.locked_by && (
          <Row label="Locked By" value={sec.locked_by === "system" ? <em>system</em> : sec.locked_by}/>
        )}
        <Row label="Last Login" value={sec?.last_login_at ? sec.last_login_at.slice(0, 19).replace("T", " ") : "Never"}/>
        <Row label="Force Password Change" value={
          <Badge tone={sec?.force_password_change ? "warning" : "neutral"} size="sm">
            {sec?.force_password_change ? "Required on next login" : "Not required"}
          </Badge>
        }/>
        <Row label="Password Last Reset" value={sec?.password_last_reset_at ? sec.password_last_reset_at.slice(0, 19).replace("T", " ") : "Never"}/>
        {sec?.password_reset_reason && (
          <Row label="Last Reset Reason" value={<span className="italic">{sec.password_reset_reason}</span>}/>
        )}
      </div>

      {/* Active Sessions */}
      <div className="hocp-card p-5" data-testid="security-sessions">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display font-semibold text-[15px] text-hocp-textPrimary">Active Sessions</div>
            <div className="text-[11px] text-hocp-textTertiary">Every logged-in browser or device.</div>
          </div>
          {isSuperAdmin && sec?.active_sessions?.length > 0 && (
            <Button size="sm" variant="outline" onClick={doTerminateAll} data-testid="terminate-all">
              End all sessions
            </Button>
          )}
        </div>
        {!sec?.active_sessions?.length && (
          <div className="text-[13px] text-hocp-textSecondary text-center py-4">
            No active sessions.
          </div>
        )}
        {sec?.active_sessions?.map(s => (
          <div key={s.id} className="flex items-center justify-between gap-3 py-2 border-b border-hocp-alice/60 last:border-b-0"
                data-testid={`session-${s.jti}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge tone="neutral" size="sm">{s.browser}</Badge>
                <Badge tone="neutral" size="sm">{s.device}</Badge>
                <span className="text-[12px] text-hocp-textSecondary tabular-nums">{s.ip_address}</span>
              </div>
              <div className="text-[11px] text-hocp-textTertiary tabular-nums mt-0.5">
                logged in {s.login_at?.slice(0, 19).replace("T", " ")} · last activity {s.last_activity_at?.slice(11, 19)}
              </div>
            </div>
            {isSuperAdmin && (
              <Button variant="ghost" size="sm" onClick={() => doTerminate(s.jti)}
                      data-testid={`terminate-${s.jti}`}>
                <X className="w-3.5 h-3.5 mr-1"/> End
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Password Reset (Super Admin) */}
      {isSuperAdmin && (
        <div className="hocp-card p-5" data-testid="admin-reset-block">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="font-display font-semibold text-[15px] text-hocp-textPrimary">Reset Password</div>
              <div className="text-[11px] text-hocp-textTertiary">Super Admin only. Terminates every active session.</div>
            </div>
            {!showReset && (
              <Button size="sm" onClick={() => setShowReset(true)} data-testid="admin-reset-open">
                <KeyRoundIcon className="w-3.5 h-3.5 mr-1"/> Reset password
              </Button>
            )}
          </div>
          {showReset && (
            <div className="space-y-3 mt-2" data-testid="admin-reset-form">
              <div>
                <label className="text-[11px] uppercase text-hocp-textTertiary font-semibold">New Password</label>
                <Input type="password" value={newPass} onChange={e => setNewPass(e.target.value)}
                        placeholder="Min 12 chars"
                        className="mt-1" data-testid="admin-reset-password"/>
              </div>
              <div>
                <label className="text-[11px] uppercase text-hocp-textTertiary font-semibold">Reason</label>
                <Input value={reason} onChange={e => setReason(e.target.value)}
                        placeholder="Why is this reset happening?"
                        className="mt-1" data-testid="admin-reset-reason"/>
              </div>
              <label className="text-[13px] flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={forceChange}
                        onChange={e => setForceChange(e.target.checked)}
                        data-testid="admin-reset-force"/>
                Force password change on next login
              </label>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowReset(false)}>Cancel</Button>
                <Button size="sm" onClick={doReset} disabled={saving} data-testid="admin-reset-confirm">
                  {saving ? "Resetting…" : "Reset Password"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Security Timeline */}
      <div className="hocp-card p-5" data-testid="security-timeline">
        <div className="font-display font-semibold text-[15px] text-hocp-textPrimary mb-3">Security Timeline</div>
        {timeline === null && (
          <div className="text-[12px] text-hocp-textSecondary">Loading…</div>
        )}
        {timeline !== null && timeline.length === 0 && (
          <div className="text-[12px] text-hocp-textSecondary text-center py-4">No security events yet.</div>
        )}
        {timeline?.length > 0 && (
          <div className="space-y-2">
            {timeline.map((t, i) => (
              <div key={i} className="flex items-start gap-3 py-1.5" data-testid={`timeline-row-${i}`}>
                <div className="w-1.5 h-1.5 rounded-full bg-hocp-honeydew mt-2"/>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-hocp-textPrimary">{t.kind}</div>
                  <div className="text-[11px] text-hocp-textTertiary tabular-nums">
                    {t.timestamp?.slice(0, 19).replace("T", " ")}
                    {t.actor_email && <> · by {t.actor_email}</>}
                    {t.ip && <> · {t.ip}</>}
                  </div>
                  {t.metadata?.reason && (
                    <div className="text-[11px] italic text-hocp-textSecondary mt-0.5">{t.metadata.reason}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityTab({ tm }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/events?entity_id=${tm.id}&limit=50`);
        const items = data.items || data || [];
        if (mounted) setEvents(items);
      } catch { if (mounted) setEvents([]); }
    })();
    return () => { mounted = false; };
  }, [tm.id]);

  if (events === null)
    return <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14"/>)}</div>;
  if (events.length === 0)
    return <div className="hocp-card p-8 text-center text-hocp-textSecondary">No recent activity for this team member.</div>;

  return (
    <div className="space-y-2" data-testid="activity-list">
      {events.map(e => (
        <div key={e._id || e.id} className="hocp-card p-3 flex items-start gap-3">
          <div className="w-2 h-2 rounded-full bg-hocp-honeydew mt-2"/>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-hocp-textPrimary">{e.name}</div>
            <div className="text-[11px] text-hocp-textTertiary tabular-nums">{(e.timestamp || "").slice(0, 19).replace("T", " ")}</div>
            {e.payload && Object.keys(e.payload).length > 0 && (
              <pre className="text-[11px] text-hocp-textSecondary font-mono mt-1 whitespace-pre-wrap break-all">
                {JSON.stringify(e.payload, null, 2)}
              </pre>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function AuditTab({ tm }) {
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/audit?entity_id=${tm.id}&limit=50`);
        const items = data.items || data || [];
        if (mounted) setLogs(items);
      } catch { if (mounted) setLogs([]); }
    })();
    return () => { mounted = false; };
  }, [tm.id]);

  if (logs === null)
    return <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14"/>)}</div>;
  if (logs.length === 0)
    return <div className="hocp-card p-8 text-center text-hocp-textSecondary">No audit entries for this team member.</div>;

  return (
    <div className="space-y-2" data-testid="audit-list">
      {logs.map(l => (
        <div key={l._id || l.id} className="hocp-card p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[13px] font-semibold text-hocp-textPrimary">{l.action}</div>
            <div className="text-[11px] text-hocp-textTertiary tabular-nums">{(l.timestamp || "").slice(0, 19).replace("T", " ")}</div>
          </div>
          <div className="text-[12px] text-hocp-textSecondary mt-0.5">
            by <span className="text-hocp-textPrimary">{l.actor_email || l.actor_id || "system"}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── main drawer ─────────────────────────────────────────────────────── */
const TABS = [
  { id: "overview",   label: "Overview",    icon: User },
  { id: "employment", label: "Employment",  icon: Briefcase },
  { id: "departments",label: "Departments", icon: Building2 },
  { id: "access",     label: "Access",      icon: ShieldCheck },
  { id: "readiness",  label: "Readiness",   icon: Gauge },
  { id: "security",   label: "Security",    icon: Lock },
  { id: "activity",   label: "Activity",    icon: Activity },
  { id: "audit",      label: "Audit",       icon: ScrollText },
];

export default function TeamMemberDrawer({
  memberId, open, onClose, onChanged,
  templates, departments, employmentTypes, permissions, isSuperAdmin = false,
}) {
  const [tm, setTm] = useState(null);
  const [tab, setTab] = useState("overview");

  const refresh = async () => {
    if (!memberId) return;
    try {
      const { data } = await api.get(`/team-members/${memberId}`);
      setTm(data);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e) || "Failed to load"); }
  };

  useEffect(() => {
    setTm(null);
    setTab("overview");
    if (memberId && open) refresh();
    // eslint-disable-next-line
  }, [memberId, open]);

  const canEdit    = permissions.has("team_members.update");
  const canAssign  = permissions.has("department_assignments.assign");
  const canRevoke  = permissions.has("department_assignments.revoke");
  const canReprim  = permissions.has("department_assignments.set_primary");
  const canApply   = permissions.has("access_templates.apply");

  const onSaveProfile = async (patch) => {
    await api.patch(`/team-members/${memberId}`, patch);
    await refresh();
  };

  const status = tm?.employment_status || (tm?.is_active ? "active" : "suspended");

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent side="right"
        className="p-0 !max-w-[1080px] w-[95vw] overflow-hidden"
        data-testid="team-member-drawer">
        {!tm && (
          <div className="p-6 space-y-4">
            <Skeleton className="h-20 w-full"/>
            <Skeleton className="h-6 w-1/3"/>
            <Skeleton className="h-64 w-full"/>
          </div>
        )}
        {tm && (
          <div className="flex h-full">
            {/* Left rail */}
            <aside className="w-[260px] shrink-0 border-r border-hocp-border bg-hocp-alice/25 p-5 overflow-y-auto">
              <div className="flex flex-col items-center text-center gap-3 pb-5 border-b border-hocp-border">
                <Avatar name={tm.name} photo_url={tm.photo_url} size={80}/>
                <div className="min-w-0 w-full">
                  <div className="font-display font-bold text-[17px] text-hocp-textPrimary truncate">{tm.name}</div>
                  <div className="text-[12px] text-hocp-textSecondary truncate">{tm.designation || "—"}</div>
                  <div className="text-[11px] font-mono tabular-nums text-hocp-textTertiary mt-1">
                    {tm.employee_id || "—"}
                  </div>
                </div>
                <div className="flex flex-wrap justify-center gap-1.5">
                  <Badge tone={STATUS_TONE[status] || "neutral"} size="sm">{status}</Badge>
                  {tm.employment_type && (
                    <Badge tone="neutral" size="sm">{tm.employment_type.toLowerCase()}</Badge>
                  )}
                </div>
              </div>
              <nav className="mt-4 space-y-1">
                {TABS.map(t => (
                  <TabButton key={t.id} id={t.id} label={t.label} icon={t.icon}
                              active={tab === t.id} onClick={setTab}
                              testid={`drawer-tab-${t.id}`}/>
                ))}
              </nav>
            </aside>

            {/* Content */}
            <main className="flex-1 overflow-y-auto p-6">
              {tab === "overview"   && <OverviewTab tm={tm}/>}
              {tab === "employment" && <EmploymentTab tm={tm} employmentTypes={employmentTypes}
                                                       canEdit={canEdit} onSave={onSaveProfile}/>}
              {tab === "departments"&& <DepartmentsTab tm={tm} departments={departments}
                                                        canAssign={canAssign} canRevoke={canRevoke}
                                                        canReprimary={canReprim} refresh={refresh}/>}
              {tab === "access"     && <AccessTab tm={tm} templates={templates}
                                                    canApply={canApply} refresh={refresh}/>}
              {tab === "readiness"  && <ReadinessTab tm={tm} isSuperAdmin={isSuperAdmin}
                                                     canManage={permissions.has("readiness.manage") || permissions.has("readiness.manage_dept") || isSuperAdmin}
                                                     canViewNotes={permissions.has("readiness.notes_view") || permissions.has("readiness.notes_view_dept") || isSuperAdmin}
                                                     isSelfView={false}/>}
              {tab === "security"   && <SecurityTab tm={tm} isSuperAdmin={isSuperAdmin} refresh={refresh}/>}
              {tab === "activity"   && <ActivityTab tm={tm}/>}
              {tab === "audit"      && <AuditTab tm={tm}/>}
            </main>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
